import React from "react";
import { Link } from "react-router-dom";
import LEGAL_CONFIG from "./legal.config";

/**
 * LegalPageLayout — shared neon/CRT shell for every /legal/* page.
 *
 * Renders the page header with title + version chip + last-updated stamp,
 * the cross-links to every other legal page (so a reader can hop
 * laterally), and the lawyer-review notice. Page-specific content slots
 * into `children`.
 *
 * Every legal page MUST go through this layout so the operator disclosure
 * (legal name + email) and the "Last updated" stamp stay consistent —
 * editing one file propagates the change site-wide.
 */
export function LegalPageLayout({ title, version, children }) {
  return (
    <main className="page-z" data-testid="legal-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/"
          className="text-[10px] tracking-widest text-white/45 hover:text-[#14b8a6]"
          data-testid="legal-back-home"
        >
          <i className="fas fa-arrow-left mr-1" />BACK TO LEAGUE HUB
        </Link>

        <header className="mt-6 pb-6 border-b border-[#14b8a6]/20">
          <h1
            className="font-display text-3xl sm:text-5xl grad-text"
            data-testid="legal-page-title"
          >
            {title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-[10px] tracking-widest text-white/40">
            {version && (
              <span className="px-2 py-0.5 rounded-full bg-[#14b8a6]/10 text-[#14b8a6]">
                v{version}
              </span>
            )}
            <span data-testid="legal-last-updated">
              LAST UPDATED · {LEGAL_CONFIG.last_updated}
            </span>
            <span className="text-amber-300/80">
              <i className="fas fa-circle-info mr-1" />
              DRAFT — lawyer review required
            </span>
          </div>
        </header>

        <article
          className="legal-prose mt-8 text-white/80 text-[14px] leading-relaxed space-y-5"
          data-testid="legal-page-content"
        >
          {children}
        </article>

        <nav className="mt-12 pt-6 border-t border-[#14b8a6]/15">
          <p className="text-[10px] tracking-widest text-white/45 mb-3">RELATED</p>
          <div className="flex flex-wrap gap-3 text-[11px]">
            {LEGAL_NAV.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-white/55 hover:text-[#14b8a6] underline-offset-2 hover:underline"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </main>
  );
}

export const LEGAL_NAV = [
  { to: "/legal/privacy",       label: "Privacy" },
  { to: "/legal/terms",         label: "Terms of Service" },
  { to: "/legal/cookies",       label: "Cookies & Tracking" },
  { to: "/legal/community",     label: "Community Guidelines" },
  { to: "/legal/subscriptions", label: "Subscription Terms" },
  { to: "/legal/withdrawal",    label: "Cancellation & Refunds" },
  { to: "/legal/contact-info",  label: "Legal / Operator Info" },
];

export default LegalPageLayout;
